import React from 'react';
import { useCurrentTrack } from '@/hooks/useCurrentTrack';
import InlineRating from './InlineRating';

/**
 * NowPlayingWidget Component
 * Displays currently playing track with album art
 * Shows track progress
 */
export const NowPlayingWidget: React.FC = () => {
  const { currentTrack, isPlaying, progressMs, isLoading } = useCurrentTrack();

  if (isLoading) {
    return (
      <div className="card animate-pulse">
        <div className="flex gap-3 md:gap-4">
          <div className="w-20 h-20 md:w-32 md:h-32 bg-gray-700 rounded-lg flex-shrink-0"></div>
          <div className="flex-1 min-w-0">
            <div className="h-5 md:h-6 bg-gray-700 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2 mb-3 md:mb-4"></div>
            <div className="h-2 bg-gray-700 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentTrack) {
    return (
      <div className="card text-center">
        <div className="py-8">
          <p className="text-2xl mb-2">🎵</p>
          <p className="text-gray-400">No track playing</p>
          <p className="text-sm text-gray-500 mt-2">
            Start playing music on Spotify to see it here
          </p>
        </div>
      </div>
    );
  }

  const albumArt = currentTrack.album.images[0]?.url;
  const progress = (progressMs / currentTrack.duration_ms) * 100;

  const formatTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="card">
      <div className="flex gap-3 md:gap-4">
        {albumArt && (
          <div className="flex-shrink-0">
            <img
              src={albumArt}
              alt={currentTrack.album.name}
              className="w-20 h-20 md:w-32 md:h-32 rounded-lg shadow-lg"
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-1 md:mb-2">
            <div className="flex-1 min-w-0 flex flex-col gap-0.5 md:gap-1">
              <h3 className="text-base md:text-xl font-bold text-white truncate">
                {currentTrack.name}
              </h3>
              <p className="text-sm md:text-base text-gray-400 truncate">
                {currentTrack.artists.map(a => a.name).join(', ')}
              </p>
              <p className="text-xs md:text-sm text-gray-500 truncate hidden sm:block">
                {currentTrack.album.name}
              </p>
              <div className="mt-0.5 md:mt-1">
                <InlineRating trackId={currentTrack.id} />
              </div>
            </div>
            <div className="flex-shrink-0 ml-2">
              {isPlaying && (
                <div className="flex items-center gap-0.5 md:gap-1">
                  <span className="w-0.5 md:w-1 h-3 md:h-4 bg-spotify-green animate-pulse rounded-full"></span>
                  <span className="w-0.5 md:w-1 h-5 md:h-6 bg-spotify-green animate-pulse rounded-full" style={{ animationDelay: '0.2s' }}></span>
                  <span className="w-0.5 md:w-1 h-2 md:h-3 bg-spotify-green animate-pulse rounded-full" style={{ animationDelay: '0.4s' }}></span>
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-2 md:mt-4">
            <div className="w-full bg-gray-700 rounded-full h-1.5 md:h-2 overflow-hidden">
              <div
                className="bg-spotify-green h-full transition-all duration-1000"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-[10px] md:text-xs text-gray-400 mt-1">
              <span>{formatTime(progressMs)}</span>
              <span>{formatTime(currentTrack.duration_ms)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NowPlayingWidget;
